import { Form, isRouteErrorResponse, redirect, useActionData, useLoaderData, useNavigate, useNavigation, useRouteError } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { api } from '../lib/api';
import ErrorScreen from '../components/ErrorScreen';
import FormError from '../components/FormError';
import AuthScene from '../components/AuthScene';
import type { InvitationPreview } from '../lib/types';

interface InvitationLoaderData {
  invite: InvitationPreview;
  token: string;
}

interface InvitationActionData {
  error?: string;
}

export async function invitationLoader({ request }: LoaderFunctionArgs): Promise<InvitationLoaderData | Response> {
  const token = new URL(request.url).searchParams.get('invite') || new URL(request.url).searchParams.get('token') || '';
  if (!token) return redirect('/signin');
  const invite = await api.invitation(token);
  return { invite, token };
}

export async function invitationAction({ request }: ActionFunctionArgs): Promise<InvitationActionData | Response> {
  const form = await request.formData();
  try {
    await api.acceptInvitation({
      token: String(form.get('token') || ''),
      name: String(form.get('name') || ''),
      email: String(form.get('email') || ''),
      password: String(form.get('password') || '')
    });
    return redirect('/');
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Could not accept the invitation.' };
  }
}

export default function Invitation() {
  const { invite, token } = useLoaderData() as InvitationLoaderData;
  const actionData = useActionData() as InvitationActionData | undefined;
  const navigation = useNavigation();

  return (
    <AuthScene
      eyebrow="You are on the call sheet"
      headline={['Ten artists.', 'One floor.']}
      ghost="One thread."
      lede="Story, world, coverage and master move through a single production thread. From here you see the same slate the rest of the crew sees."
    >
        <span className="eyebrow">Team invitation</span>
        <h1>Join {invite.studioName}</h1>
        <p className="auth-sub">You were invited as {invite.role}. Create your account to enter the shared project workspace.</p>
        <Form method="post" className="auth-form" replace>
          <FormError message={actionData?.error} />
          <input type="hidden" name="token" value={token} />
          <label className="field">
            <span>Name</span>
            <input className="input" name="name" required autoFocus />
          </label>
          <label className="field">
            <span>Email</span>
            <input className="input" name="email" type="email" readOnly defaultValue={invite.email} />
            <small>This invitation is addressed to this email.</small>
          </label>
          <label className="field">
            <span>Password</span>
            <input className="input" name="password" type="password" minLength={10} required autoComplete="new-password" />
            <small>At least 10 characters.</small>
          </label>
          <button className="app-button primary" disabled={navigation.state !== 'idle'}>
            Join studio
          </button>
        </Form>
    </AuthScene>
  );
}

export function InvitationError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const message = isRouteErrorResponse(error)
    ? String(error.data || '')
    : error instanceof Error
      ? error.message
      : '';
  const spent = /expired|already|accepted|used|not found|invalid/i.test(message);

  return (
    <ErrorScreen
      title={spent ? 'This invitation is no longer valid' : 'This invitation could not be opened'}
      detail={
        spent
          ? 'Invitations expire once they are used or withdrawn. Ask a studio owner to send you a fresh link, or sign in if you already have an account.'
          : 'The studio could not read this invitation link. Check that you pasted the whole URL, then try again.'
      }
      action={{ label: 'Go to sign in', onClick: () => navigate('/signin') }}
      secondary={spent ? undefined : { label: 'Try again', onClick: () => navigate(0) }}
    />
  );
}
